import { ApolloError, MutationHookOptions, useMutation } from '@apollo/client';
import {
  CREATE_SCHEDULE_REMINDER,
  CREATE_SCHEDULE_REMINDER_LINK,
  UPDATE_SCHEDULE_REMINDER,
  UPDATE_SCHEDULE_REMINDER_LINK,
} from 'apollo/mutations/schedule-reminder-mutations';
import { QUERY_SCHEDULE_REMINDERS } from 'apollo/queries/schedule-reminder-queries';
import { useApolloPageAll } from 'hooks';
import useScheduleReminderUpdate from 'hooks/models/schedule-reminder/useScheduleReminderUpdate/useScheduleReminderUpdate';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { parse } from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { IMember } from 'types';
import ICostCode from 'types/CostCode';
import ScheduleReminderType from 'types/enum/ScheduleReminderType';
import IEquipment from 'types/Equipment';
import IProject from 'types/Project';
import { IScheduleReminder, IScheduleReminderCreateInput } from 'types/ScheduleReminder';
import { IScheduleReminderLink, IScheduleReminderLinkUpdateInput } from 'types/ScheduleReminderLink';
import ITimeRange from 'types/TimeRange';
import { QueryParam } from 'utils/constants/queryParam';
import {
  convertDateTimeToIso,
  convertDateTimeToIsoForServer,
  convertDateToIso,
  getDateTimesBetween,
  getDayWithTimePickerValue,
  getDayWithTimePickerValueSetToIso,
  isoTimeStampUtc,
} from 'utils/dateUtils';
import { assignIfDefined } from 'utils/objectUtils';
import { getQueryParam } from 'utils/queryStringUtils';
import uuid from 'uuid';
import useActiveMember from '../../../hooks/store/useActiveMember';
import useOrganization from '../../../hooks/store/useOrganization';
import {
  IScheduleReminderDetails,
  ScheduleCreate,
  ScheduleLinkDelete,
  SchedulesCreate,
  SchedulesDelete,
  ScheduleUpdate,
  ScheduleUpdateOptimistically,
  ScheduleUpdateTime,
} from '../types/types';
import { getSchedulingTimeRangeFromParamString } from '../utils/utils';
import useCalendarView from './useCalendarView';
import useSchedulingDataFilter from './useSchedulingDataFilter';
import useSchedulingReminderPopulate from './useSchedulingReminderPopulate';

export interface ISchedulingPayload {
  loading: boolean;
  error: string | ApolloError | null | undefined;
  reminderDetails: IScheduleReminderDetails[];
  createSchedule: ScheduleCreate;
  createSchedules: SchedulesCreate;
  updateScheduleTime: ScheduleUpdateTime;
  updateSchedule: ScheduleUpdate;
  updateScheduleOptimistically: ScheduleUpdateOptimistically;
  deleteScheduleLink: ScheduleLinkDelete;
  deleteSchedules: SchedulesDelete;
  refetch: () => void;
}

export default function useScheduling(): ISchedulingPayload {
  const location = useLocation();
  const currentView = useCalendarView();
  const [filteredData, setFilteredData] = useState<IScheduleReminderDetails[]>([]);
  const [range, setRange] = useState<ITimeRange<DateTime>>(
    getTimeRange(getQueryParam(parse(location.search), QueryParam.TIME, DateTime.local().toFormat('M-d-yyyy')))
  );
  const currentTime = useRef<string>();
  const parsedData = useRef<IScheduleReminderDetails[]>([]);
  const activeMember = useActiveMember();
  const organization = useOrganization();
  const filterData = useSchedulingDataFilter();
  const parsePayload = useSchedulingReminderPopulate();

  const [createReminderMutation] = useMutation(CREATE_SCHEDULE_REMINDER);
  const [createReminderLinkMutation] = useMutation(CREATE_SCHEDULE_REMINDER_LINK);
  const [updateReminderMutation] = useMutation(UPDATE_SCHEDULE_REMINDER);
  const [updateReminderLinkMutation] =
    useMutation<{ scheduleReminderLink: IScheduleReminderLinkUpdateInput }>(UPDATE_SCHEDULE_REMINDER_LINK);
  const [err, setErr] = useState<string | null | undefined>('');
  const [t] = useTranslation();

  const {
    data: scheduleReminders,
    loading,
    error,
    refetch,
  } = useApolloPageAll<IScheduleReminder>(
    {
      query: QUERY_SCHEDULE_REMINDERS,
      fetchPolicy: 'network-only',
      variables: {
        first: 200,
        filter: {
          deletedOn: { isNull: true },
          links: {
            deletedOn: { isNull: true },
            member: { archivedOn: { isNull: true } },
          },
          startTime: {
            lessThanOrEqual: range
              ? range.endTime.toISO({ suppressMilliseconds: true, includeOffset: false })
              : DateTime.local().startOf('month').toISO({ suppressMilliseconds: true, includeOffset: false }),
          },
          endTime: {
            greaterThanOrEqual: range
              ? range.startTime.toISO({ suppressMilliseconds: true, includeOffset: false })
              : DateTime.local().endOf('month').toISO({ suppressMilliseconds: true, includeOffset: false }),
          },
        },
        sort: [{ startTime: 'asc' }],
      },
    },
    'scheduleReminders'
  );

  useEffect(() => {
    const params = parse(location.search);
    const newTime = getQueryParam(params, QueryParam.TIME, DateTime.local().toFormat('M-d-yyyy'));
    const timeRange = getTimeRange(newTime);

    if (
      timeRange.startTime.toSeconds() !== range.startTime.toSeconds() ||
      timeRange.endTime.toSeconds() !== range.endTime.toSeconds()
    ) {
      currentTime.current = newTime;
      setRange(getTimeRange(newTime));
    } else {
      // Need to evaluate the query param changes from the filter.
      setFilteredData(filterData(parsedData.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, currentView]);

  useEffect(() => {
    if (scheduleReminders) {
      parsePayload(scheduleReminders, () => {
        setErr(t('There was a problem populating your schedules. Try again later.') as string);
      }).then((res) => {
        parsedData.current = res;
        setFilteredData(filterData(parsedData.current));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleReminders]);

  function getTimeRange(time: string) {
    return getSchedulingTimeRangeFromParamString(time, currentView);
  }

  async function deleteSchedules(ids: string[]) {
    await Promise.all(ids.map((id) => deleteSchedule(id)));
  }

  async function deleteSchedule(id: string) {
    const item = filteredData.find((reminder) => reminder.id === id);

    if (item) {
      const promises = item.links.map((link) =>
        updateReminderLinkMutation({ variables: { link: { deletedOn: isoTimeStampUtc(), id: link.id } } })
      );
      promises.push(updateReminderMutation({ variables: { reminder: { id: item.id, deletedOn: isoTimeStampUtc() } } }));

      await Promise.all(promises);
      await refetch();
    }
  }

  async function createSchedule(
    day: DateTime,
    startTime: string,
    endTime: string,
    memberIds: string[],
    type: ScheduleReminderType,
    notifyEmployee: boolean,
    payload: string | null,
    fetchAfter: boolean
  ) {
    const start = getDayWithTimePickerValueSetToIso(day, startTime);
    const end = getDayWithTimePickerValueSetToIso(day, endTime);

    const createdOn = isoTimeStampUtc();

    // If start is before end we need to make one schedule that's from the start time to midnight and another that's from midnight to the end time
    const s = DateTime.fromISO(start);
    const e = DateTime.fromISO(end);

    const scheduleReminderId = uuid();

    const reminder: IScheduleReminderCreateInput = {
      id: scheduleReminderId,
      startTime: start,
      endTime: s.toSeconds() > e.toSeconds() ? e.plus({ day: 1 }).toISO() : e.toISO(),
      notifyEmployee,
      memberId: activeMember.id!,
      organizationId: organization.id!,
      createdOn,
      payload: payload ? payload : undefined,
      type,
    };

    await createReminderMutation({ variables: { reminder } });
    await Promise.all(
      memberIds.map((memberId) =>
        createReminderLinkMutation({ variables: { link: { memberId, scheduleReminderId: reminder.id!, createdOn } } })
      )
    );

    if (fetchAfter) {
      await refetch();
    }
  }

  async function createSchedules(
    startDate: Date,
    endDate: Date | undefined,
    startTime: string,
    endTime: string,
    members: string[],
    type: ScheduleReminderType,
    notifyEmployee: boolean,
    payload: string | null
  ) {
    if (endDate) {
      // Need to create multiple schedule items
      const start = DateTime.fromJSDate(startDate).startOf('day');
      const end = DateTime.fromJSDate(endDate).startOf('day');
      const promises = getDateTimesBetween(start, end).map((date) =>
        createSchedule(date, startTime, endTime, members, type, notifyEmployee, payload, false)
      );
      await Promise.all(promises);
    } else {
      await createSchedule(
        DateTime.fromJSDate(startDate),
        startTime,
        endTime,
        members,
        type,
        notifyEmployee,
        payload,
        false
      );
    }

    await refetch();
  }
  const [, updateScheduleReminder] = useScheduleReminderUpdate();

  async function updateScheduleTime<T extends Date | DateTime>(id: string, start: T, end: T) {
    const startTime = start instanceof Date ? convertDateToIso(start) : convertDateTimeToIso(start as DateTime);
    const endTime = end instanceof Date ? convertDateToIso(end) : convertDateTimeToIso(end as DateTime);

    const index = filteredData.findIndex((event) => event.id === id);
    const newEvents = [...filteredData];
    newEvents[index] = { ...filteredData[index], startTime, endTime };

    setFilteredData(newEvents);

    await updateScheduleReminder({ id, startTime, endTime });
    await refetch();
  }

  // Undefined will use the default, null will set it to null!
  async function updateScheduleOptimistically(
    id: string,
    originalPayload?: string | null,
    startTime?: string,
    endTime?: string,
    members?: IMember[],
    project?: IProject | null,
    costCode?: ICostCode | null,
    equipment?: IEquipment | null,
    notifyEmployee?: boolean | null,
    options?: Omit<MutationHookOptions, 'variables'>
  ) {
    const reminderDetails: { [key: string]: any } = { id };
    const payload: { [key: string]: string } = originalPayload ? JSON.parse(originalPayload) : {};

    assignIfDefined(payload, 'project', project !== undefined ? project?.id ?? null : undefined);
    assignIfDefined(payload, 'cost_code', costCode !== undefined ? costCode?.id ?? null : undefined);
    assignIfDefined(payload, 'equipment', equipment !== undefined ? equipment?.id ?? null : undefined);

    reminderDetails.payload = JSON.stringify(payload);

    assignIfDefined(reminderDetails, 'startTime', startTime);
    assignIfDefined(reminderDetails, 'endTime', endTime);
    assignIfDefined(reminderDetails, 'notifyEmployee', notifyEmployee);

    // Need to update before assigning all the other stuff.
    const updateReminder = updateReminderMutation({ ...options, variables: { reminder: { ...reminderDetails } } });

    assignIfDefined(reminderDetails, 'project', project);
    assignIfDefined(reminderDetails, 'costCode', costCode);
    assignIfDefined(reminderDetails, 'equipment', equipment);

    const index = filteredData.findIndex((event) => event.id === id);

    if (members) {
      const currentEvent = filteredData[index];
      const currentMembers = currentEvent.links.map((link) => link.member);
      const deletingMembers = _.differenceBy(currentMembers, members, 'id');

      const creatingMembers = _.differenceBy(members, currentMembers, 'id');
      const time = isoTimeStampUtc();

      const linksToDelete = deletingMembers.map((memberToDelete) =>
        _.find(currentEvent.links, (link) => memberToDelete.id === link.member.id)
      );

      const deletingLinkPromises: Array<Promise<any>> = linksToDelete.map(async (linkToDelete) => {
        if (linkToDelete) {
          return deleteScheduleLink(linkToDelete.id);
        } else {
          return null;
        }
      });

      const linksToCreate = creatingMembers.map<IScheduleReminderLink>((memberToCreate) => ({
        id: uuid(),
        scheduleReminderId: currentEvent.id,
        memberId: memberToCreate.id,
        createdOn: time,
        member: memberToCreate,
        updatedOn: null,
        submittedOn: null,
      }));

      const createPromises = linksToCreate.map((link) => {
        const withoutMember = {
          id: link.id,
          scheduleReminderId: link.scheduleReminderId,
          memberId: link.memberId,
          createdOn: link.createdOn,
        };

        return createReminderLinkMutation({ variables: { link: withoutMember } });
      });

      const links = currentEvent.links
        .filter((link) => linksToDelete.length === 0 || !linksToDelete.some((toDelete) => toDelete?.id === link.id))
        .concat(linksToCreate);

      setFilteredData((currentData) => {
        const newEvents = [...currentData];
        newEvents[index] = { ...currentData[index], links, ...reminderDetails };
        return newEvents;
      });

      await Promise.all([...deletingLinkPromises, ...createPromises]);

      if (links.length > 0) {
        await updateReminder;
      } else {
        await deleteSchedule(id);
      }
    } else {
      setFilteredData((currentData) => {
        const newEvents = [...currentData];
        newEvents[index] = { ...currentData[index], ...reminderDetails };

        return newEvents;
      });

      await updateReminder;
    }

    return refetch();
  }

  async function updateSchedule(
    editingScheduleItem: IScheduleReminder,
    members: string[],
    startTime: string,
    endTime: string,
    notifyEmployee: boolean,
    payload: string | null,
    startDate: Date,
    type: ScheduleReminderType
  ) {
    const day = DateTime.fromJSDate(startDate).startOf('day');
    const end = DateTime.fromFormat(endTime, 'H:mm');

    const updateOriginalReminderPromises = updateReminderMutation({
      variables: {
        reminder: {
          id: editingScheduleItem.id,
          payload,
          startTime: getDayWithTimePickerValueSetToIso(day, startTime),
          endTime:
            end.toSeconds() >= DateTime.fromFormat(startTime, 'H:mm').toSeconds()
              ? getDayWithTimePickerValueSetToIso(day, endTime)
              : convertDateTimeToIsoForServer(getDayWithTimePickerValue(day, endTime).plus({ day: 1 })),
          notifyEmployee,
          type,
        },
      },
    });

    // If the members are different we need to create a link and delete links
    const currentMembers = editingScheduleItem.links.map((link) => link.member.id);

    const time = isoTimeStampUtc();

    const deletingLinkPromises = _.difference(currentMembers, members)
      .map((memberToDelete) => _.find(editingScheduleItem.links, (link) => memberToDelete === link.member.id))
      .map((linkToDelete) => {
        if (linkToDelete) {
          return updateReminderLinkMutation({ variables: { link: { id: linkToDelete.id, deletedOn: time } } });
        }
      });

    const linksToCreatePromises = _.difference(members, currentMembers).map((memberToCreate) => {
      return createReminderLinkMutation({
        variables: {
          link: {
            scheduleReminderId: editingScheduleItem.id,
            memberId: memberToCreate,
            createdOn: time,
          },
        },
      });
    });

    await Promise.all([updateOriginalReminderPromises, ...deletingLinkPromises, ...linksToCreatePromises]);
    await refetch();
  }

  async function deleteScheduleLink(id: string) {
    const result = await updateReminderLinkMutation({ variables: { link: { id, deletedOn: isoTimeStampUtc() } } });
    return result.data?.scheduleReminderLink;
  }

  return {
    loading,
    error: error ?? err,
    reminderDetails: filteredData,
    createSchedule,
    createSchedules,
    updateScheduleTime,
    updateSchedule,
    updateScheduleOptimistically,
    deleteSchedules,
    deleteScheduleLink,
    refetch,
  };
}
